import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { AuthService } from '@core/auth/auth.service';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ApiSysLocationService } from '@core/api/api-sys-location.service';
import { ApiSysService } from '@core/api/api-sys.service';
import { AircraftService, InfrastructuresService, PersonnelService } from '@core/api/generated';
import type {
  AircraftListItem,
  ConsentAnswer,
  ConsentStatusRead,
  DialCodeRead,
  OrganizationListItem,
  PersonnelAircraftRead,
  PersonnelCreate,
  PersonnelInfrastructureRead,
  PersonnelListItem,
  PersonnelRead,
  PersonnelRoleRead,
  PersonnelUpdate,
  PreferencesRead,
  RoleCategoryRead,
} from '@core/api/generated';
import { OrganizationApi } from '@features/resources/organizations/data/organization.api';
import { PersonnelMapper } from './personnel.mapper';
import { isHeldGrant } from '../models/personnel.model';
import type { UserPersonnel } from '@core/auth/auth.service';

export interface RolesUpdateResult {
  grants: PersonnelRoleRead[];
  /** Codes submitted (and approved, for admins) as new grants. */
  added: string[];
  /** Codes whose grants were deleted. */
  removed: string[];
  /** Desired codes that could not be added: the org catalog has no role for them. */
  unresolved: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PersonnelApi {
  private readonly api = inject(PersonnelService);
  private readonly aircraftService = inject(AircraftService);
  private readonly infrastructureService = inject(InfrastructuresService);
  private readonly legacyApi = inject(ApiSysService);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly locationApi = inject(ApiSysLocationService);
  private readonly organizationApi = inject(OrganizationApi);
  private readonly mapper = inject(PersonnelMapper);

  private get gatewayUrl(): string {
    return environment.url_gateway_api;
  }

  private get authHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    return token ? new HttpHeaders().set('Authorization', `Bearer ${token}`) : new HttpHeaders();
  }

  getPersonnel(
    q?: string,
    limit?: number,
    offset?: number,
  ): Observable<{ items: PersonnelListItem[]; total: number }> {
    return this.api
      .listPersonnelApiV1PersonnelGet(q, undefined, undefined, undefined, limit, offset)
      .pipe(map((page) => ({ items: page.items, total: page.total })));
  }

  /**
   * Loads the authenticated user's own profile from the v2 endpoint
   * (GET /api/v1/personnel/me) and maps it to the legacy `UserPersonnel` shape
   * consumed by `AuthService`/the app shell. Replaces the legacy bootstrap
   * `ApiSysService.getPersonnelSelf()` (`/approutes/personnel/me`), which the v2
   * gateway does not serve.
   */
  getSelf(): Observable<UserPersonnel> {
    return this.api
      .getCurrentPersonnelApiV1PersonnelMeGet()
      .pipe(map((me) => this.mapper.toAuthUser(me)));
  }

  acceptAllConsents(): Observable<ConsentStatusRead> {
    return this.api.getOwnConsentsApiV1PersonnelMeConsentsGet().pipe(
      switchMap((status) =>
        this.api.recordOwnConsentsApiV1PersonnelMeConsentsPost({
          consents: status.consents.map((c) => ({
            document_type: c.document_type as ConsentAnswer['document_type'],
            approved: true,
          })),
        }),
      ),
    );
  }

  getPersonnelById(id: number): Observable<PersonnelRead> {
    return this.api.getPersonnelApiV1PersonnelPersonnelIdGet(id);
  }

  getOrganizationById(id: number): Observable<any> {
    return this.organizationApi.getOrganizationById(id);
  }

  getDialCodes(): Observable<DialCodeRead[]> {
    return this.organizationApi.getDialCodes();
  }

  getAllOrgsSimple(): Observable<OrganizationListItem[]> {
    return this.organizationApi
      .fetchOrganizations(undefined, 200)
      .pipe(map((page) => page.items));
  }

  getAircraftByPersonnelId(id: number): Observable<PersonnelAircraftRead[]> {
    return this.api
      .listPersonnelAircraftApiV1PersonnelPersonnelIdAircraftGet(id)
      .pipe(map((page) => page.items));
  }

  getInfrastructuresByUserId(id: number): Observable<PersonnelInfrastructureRead[]> {
    return this.api
      .listPersonnelInfrastructuresApiV1PersonnelPersonnelIdInfrastructuresGet(id)
      .pipe(map((page) => page.items));
  }

  getRolesForOrganization(organizationId: number): Observable<RoleCategoryRead[]> {
    return this.organizationApi.getRoles(organizationId);
  }

  getPersonnelPreferences(personnelId: number): Observable<PreferencesRead> {
    if (!this.isOwnProfile(personnelId)) {
      return of({ preferred_url: null });
    }
    return this.api
      .getCurrentPersonnelApiV1PersonnelMeGet()
      .pipe(map((me) => me.preferences ?? { preferred_url: null }));
  }

  updatePersonnelPreferences(
    personnelId: number,
    preferences: { preferred_url?: string | null },
  ): Observable<PreferencesRead | null> {
    if (!this.isOwnProfile(personnelId)) {
      return of(null);
    }
    return this.api.updateOwnPreferencesApiV1PersonnelMePreferencesPatch(preferences);
  }

  private isOwnProfile(personnelId: number): boolean {
    return Number(this.authService.getUserId()) === Number(personnelId);
  }

  searchModelLocation(model: string, search: string): Observable<any> {
    return this.locationApi.searchModelLocation(model, search);
  }

  createPersonnel(payload: PersonnelCreate): Observable<PersonnelRead> {
    return this.api.createPersonnelApiV1PersonnelPost(payload);
  }

  updatePersonnel(id: number, payload: PersonnelUpdate): Observable<PersonnelRead> {
    return this.api.autosavePersonnelApiV1PersonnelPersonnelIdPatch(id, payload);
  }

  deactivatePersonnel(id: number): Observable<PersonnelRead> {
    return this.api.deactivatePersonnelApiV1PersonnelPersonnelIdDeactivatePost(id);
  }

  reactivatePersonnel(id: number): Observable<PersonnelRead> {
    return this.api.reactivatePersonnelApiV1PersonnelPersonnelIdReactivatePost(id);
  }

  submitPersonnel(id: number): Observable<PersonnelRead> {
    return this.api.submitPersonnelApiV1PersonnelPersonnelIdSubmitPost(id);
  }

  approvePersonnel(id: number): Observable<PersonnelRead> {
    return this.api.approvePersonnelApiV1PersonnelPersonnelIdApprovePost(id);
  }

  rejectPersonnel(id: number): Observable<PersonnelRead> {
    return this.api.rejectPersonnelApiV1PersonnelPersonnelIdRejectPost(id);
  }

  deletePersonnel(id: number): Observable<any> {
    return this.api.deletePersonnelApiV1PersonnelPersonnelIdDelete(id);
  }

  updateRoles(
    personnelId: number,
    organizationId: number,
    desiredCodes: string[],
    options: { approve: boolean },
  ): Observable<RolesUpdateResult> {
    return forkJoin({
      listed: this.api.listRolesApiV1PersonnelPersonnelIdRolesGet(personnelId),
      personnel: this.api.getPersonnelApiV1PersonnelPersonnelIdGet(personnelId),
      catalog: this.getRolesForOrganization(organizationId),
    }).pipe(
      switchMap(({ listed, personnel, catalog }) => {
        const current: PersonnelRoleRead[] = [...listed];
        (personnel.roles ?? []).forEach((grant) => {
          if (!current.some((r) => r.role_id === grant.role_id)) current.push(grant);
        });

        const held = current.filter(isHeldGrant);
        const heldCodes = new Set(held.map((r) => r.code));
        const codeToRoleId = new Map<string, number>();
        catalog.forEach((category) =>
          (category.roles ?? []).forEach((role) => codeToRoleId.set(role.code, role.role_id)),
        );

        const codesToAdd = [...new Set(desiredCodes)].filter((code) => !heldCodes.has(code));
        const grantsToRemove = held.filter((grant) => !desiredCodes.includes(grant.code));
        const addable = codesToAdd.filter((code) => codeToRoleId.has(code));
        const unresolved = codesToAdd.filter((code) => !codeToRoleId.has(code));
        const roleIdsToAdd = [...new Set(addable.map((code) => codeToRoleId.get(code)!))];
        const removed = grantsToRemove.map((grant) => grant.code);

        const removeCalls = grantsToRemove.map((grant) =>
          this.api.removeRoleApiV1PersonnelPersonnelIdRolesRoleIdDelete(personnelId, grant.role_id),
        );
        const afterRemovals = (grants: PersonnelRoleRead[]): Observable<RolesUpdateResult> => {
          const result: RolesUpdateResult = { grants, added: addable, removed, unresolved };
          return removeCalls.length > 0
            ? forkJoin(removeCalls).pipe(map(() => result))
            : of(result);
        };

        if (roleIdsToAdd.length === 0) {
          return afterRemovals(current);
        }

        return this.api
          .submitRolesApiV1PersonnelPersonnelIdRolesSubmitPost(personnelId, {
            role_ids: roleIdsToAdd,
          })
          .pipe(
            switchMap((submitted) =>
              options.approve
                ? this.api.approveRolesApiV1PersonnelPersonnelIdRolesApprovePost(personnelId, {
                    role_ids: roleIdsToAdd,
                  })
                : of(submitted),
            ),
            switchMap((grants) => afterRemovals(grants)),
          );
      }),
    );
  }

  forceLogoutPersonnel(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.gatewayUrl}/approutes/auth/logout/${id}`, {}, { headers });
  }

  assignAircraftToPersonnel(personnelId: number, aircraftId: number): Observable<any> {
    const isDirectAssign = this.authService.isSuperAdmin() || this.authService.isAdmin();
    return this.aircraftService.addPersonnelLinkApiV1AircraftAircraftIdPersonnelPost(
      aircraftId,
      isDirectAssign ? { personnel_id: personnelId } : undefined,
    );
  }

  removeAircraftFromPersonnel(personnelId: number, aircraftId: number): Observable<any> {
    return this.aircraftService.removePersonnelLinkApiV1AircraftAircraftIdPersonnelPersonnelIdDelete(
      aircraftId,
      personnelId,
    );
  }

  assignInfrastructureToPersonnel(personnelId: number, infrastructureId: number): Observable<any> {
    const isDirectAssign = this.authService.isSuperAdmin() || this.authService.isAdmin();
    return this.infrastructureService.addPersonnelLinkApiV1InfrastructuresInfrastructureIdPersonnelPost(
      infrastructureId,
      isDirectAssign ? { personnel_id: personnelId } : undefined,
    );
  }

  removeInfrastructureFromPersonnel(
    personnelId: number,
    infrastructureId: number,
  ): Observable<any> {
    return this.infrastructureService.removePersonnelLinkApiV1InfrastructuresInfrastructureIdPersonnelPersonnelIdDelete(
      infrastructureId,
      personnelId,
    );
  }
}
